// ============================================================
//  src/Wiki/Wiki.js  —  SimpleMC Unified Wiki
//  Items · Mobs · Drops — searchable, filterable,
//  with full detail views and crafting recipe panel.
// ============================================================

import '../housestyle.css';
import './Wiki.css';
import ALL_ITEMS from '../data/items';
import mobs from '../data/mobs';
import { useMemo, useState } from 'react';

// ── Helpers ─────────────────────────────────────────────────
const ITEM_EMOJI = {
  Sword: '⚔️', Axe: '🪓', Bow: '🏹', Crossbow: '🏹',
  Chestplate: '🛡️', Helmet: '⛑️', Leggings: '🦺', Boots: '👟',
  Potion: '🧪', Tool: '⛏️', Pickaxe: '⛏️',
};

const TIER_COLOR = {
  common: 'var(--tier-common)', uncommon: 'var(--tier-uncommon)',
  rare: 'var(--tier-rare)', elite: '#7ab8c4',
  epic: 'var(--tier-epic)', legendary: 'var(--tier-legendary)', boss: 'var(--tier-boss)',
};

const RARITY_LABEL = {
  common: 'Common', uncommon: 'Uncommon', rare: 'Rare',
  elite: 'Elite', epic: 'Epic', legendary: 'Legendary', boss: 'Boss',
};

function itemIcon(item) {
  return item.icon || ITEM_EMOJI[item.type] || ITEM_EMOJI[item.category] || '📦';
}

function barColor(pct) {
  if (pct >= 80) return '#e84c3d';
  if (pct >= 55) return '#f0ab20';
  if (pct >= 35) return '#d4920a';
  return '#52c078';
}

// ── Derived: flatten all mob drops into a searchable list ────
const ALL_DROPS = mobs.flatMap(mob =>
  (mob.drops || []).map(drop => ({
    ...drop,
    id: `${mob.id}--${drop.name.toLowerCase().replace(/\s+/g, '-')}`,
    sourceMobId:   mob.id,
    sourceMobName: mob.name,
    sourceMobIcon: mob.icon,
    sourceMobTier: mob.tier,
  }))
);

// ── Shared: Stat Bar ─────────────────────────────────────────
function StatBar({ label, value, max, unit = '', color }) {
  const pct  = Math.min(100, Math.round((value / max) * 100));
  const fill = color || barColor(pct);
  return (
    <div className="wiki-stat-row">
      <div className="wiki-stat-header">
        <span className="wiki-stat-label">{label}</span>
        <span className="wiki-stat-value">{value}{unit}</span>
      </div>
      <div className="stat-bar-track">
        <div className="stat-bar-fill" style={{ width: `${pct}%`, background: fill }} />
      </div>
    </div>
  );
}

// ── Shared: Expandable Ability Card ──────────────────────────
function AbilityCard({ ability }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="wiki-ability" onClick={() => setOpen(o => !o)}>
      <div className="wiki-ability-header">
        <div className="wiki-ability-left">
          <span className="wiki-ability-icon">{ability.icon || '✨'}</span>
          <div>
            <div className="wiki-ability-name">{ability.name}</div>
            <div className="wiki-ability-type">{ability.type}</div>
          </div>
        </div>
        <span className={`wiki-ability-chevron${open ? ' open' : ''}`}>▼</span>
      </div>
      {open && (
        <div className="wiki-ability-body">
          {ability.description}
          {ability.cooldown && (
            <div className="wiki-ability-cooldown">Cooldown: {ability.cooldown}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Recipe Panel (ingredient list view) ──────────────────────
function RecipePanel({ recipe }) {
  if (!recipe) return null;

  const isUnknown = !recipe.ingredients?.length
    || recipe.craftingType === 'Unknown'
    || recipe.craftingType === 'Starter Item';

  return (
    <div className="wiki-recipe-card">
      <div className="wiki-recipe-type">
        🔨 {recipe.craftingType ?? 'Crafting Table'}
      </div>

      {isUnknown ? (
        <div style={{ fontSize: 'var(--t-xs)', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          {recipe.note ?? 'Recipe not yet revealed.'}
        </div>
      ) : (
        <>
          <div className="wiki-recipe-ingredients">
            {recipe.ingredients.map((ing, i) => (
              <div key={i} className="wiki-ingredient-chip">
                <span>{ing.name}</span>
                {ing.qty > 1 && <span className="wiki-ingredient-qty">×{ing.qty}</span>}
              </div>
            ))}
          </div>
          {recipe.note && (
            <div className="wiki-recipe-note">ℹ️ {recipe.note}</div>
          )}
        </>
      )}
    </div>
  );
}

// ── Section divider used in "All" tab ────────────────────────
function SectionDivider({ label }) {
  return (
    <div className="wiki-section-label">
      <span className="wiki-section-label-text">{label}</span>
      <div className="wiki-section-label-line" />
    </div>
  );
}

// ── DETAIL: Item ─────────────────────────────────────────────
function ItemDetail({ item, onBack }) {
  const glowColor = item.glowColor ?? 'rgba(192,57,43,0.10)';
  const icon = itemIcon(item);

  const stats = item.stats ?? [
    item.damage      > 0 ? { label: 'Damage',       value: item.damage,      max: item.damageMax      || 30,   unit: ' DMG' } : null,
    item.attackSpeed > 0 ? { label: 'Attack Speed',  value: item.attackSpeed, max: item.attackSpeedMax || 3,    unit: '/s'   } : null,
    item.durability  > 0 ? { label: 'Durability',    value: item.durability,  max: item.durabilityMax  || 3000, unit: ''     } : null,
  ].filter(Boolean);

  const obtainList = item.obtainMethods ?? item.obtainedFrom ?? [];

  return (
    <div style={{ minHeight: '100vh' }}>
      <div className="container" style={{ paddingTop: '1.5rem' }}>
        <button className="btn btn-secondary" onClick={onBack} style={{ fontSize: 'var(--t-xs)' }}>
          ← Back to Wiki
        </button>
      </div>

      {/* Hero */}
      <div className="wiki-detail-hero">
        <div className="wiki-detail-glow"
          style={{ background: `radial-gradient(circle, ${glowColor} 0%, transparent 68%)` }} />
        <div className="container">
          <div className="wiki-detail-inner">
            <div className="wiki-detail-icon-box">{icon}</div>
            <div className="wiki-detail-meta">
              <div className="wiki-detail-badges">
                <span className="wiki-cat-badge wiki-cat-item">Item</span>
                <span className={`tier-badge tier-${item.rarity}`}>
                  {RARITY_LABEL[item.rarity] ?? item.rarity}
                </span>
                {item.type && <span className="wiki-type-tag">{item.type}</span>}
                {item.category && item.category !== item.type && (
                  <span className="wiki-type-tag">{item.category}</span>
                )}
              </div>
              <h1 className="wiki-detail-name">{item.name}</h1>
              <p className="wiki-detail-desc">{item.description}</p>
              {item.tags?.length > 0 && (
                <div className="wiki-detail-tags">
                  {item.tags.map((t, i) => <span key={i} className="wiki-tag-pill">{t}</span>)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="container">
        <div className="wiki-detail-body">

          {/* Left column: stats, abilities, lore, changelog */}
          <div>
            {stats.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <div className="section-label"><span className="sl-text">Stats</span></div>
                <div className="card-raised">
                  {stats.map((s, i) => (
                    <StatBar key={i} label={s.label} value={s.value} max={s.max} unit={s.unit ?? ''} color={s.color} />
                  ))}
                </div>
              </div>
            )}

            {item.abilities?.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <div className="section-label"><span className="sl-text">Abilities</span></div>
                {item.abilities.map((ab, i) => <AbilityCard key={i} ability={ab} />)}
                <p style={{ fontSize: 'var(--t-xs)', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                  Click an ability to expand
                </p>
              </div>
            )}

            {item.lore && (
              <div style={{ marginBottom: '2rem' }}>
                <div className="section-label"><span className="sl-text">Lore</span></div>
                <div className="wiki-lore">"{item.lore}"</div>
              </div>
            )}

            {item.history?.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <div className="section-label"><span className="sl-text">Changelog</span></div>
                <div className="card-raised" style={{ padding: '0.25rem 1.25rem' }}>
                  {item.history.map((h, i) => (
                    <div key={i} className="wiki-changelog-row">
                      <div className="wiki-changelog-header">
                        <span className="wiki-changelog-version">{h.version}</span>
                        <span className="wiki-changelog-date">{h.date}</span>
                      </div>
                      {h.changes.map((c, j) => (
                        <div key={j} className="wiki-changelog-change">
                          <span className="wiki-changelog-dot">·</span>{c}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column: recipe, how to obtain */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {item.recipe && (
              <div>
                <div className="section-label"><span className="sl-text">Crafting Recipe</span></div>
                <RecipePanel recipe={item.recipe} />
              </div>
            )}

            {obtainList.length > 0 && (
              <div>
                <div className="section-label"><span className="sl-text">How to Obtain</span></div>
                <div className="card-raised" style={{ padding: '0.25rem 1.25rem' }}>
                  {obtainList.map((m, i) => (
                    <div key={i} className="wiki-obtain-row">
                      <span className="wiki-obtain-arrow">›</span>
                      {m}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── DETAIL: Mob ──────────────────────────────────────────────
function MobDetail({ mob, onBack }) {
  const { stats } = mob;
  const statBars = [
    { label: 'Health',  value: stats.hp,      max: stats.hpMax,   color: '#52c078' },
    { label: 'Damage',  value: stats.damage,   max: stats.dmgMax,  color: 'var(--tier-boss)' },
    { label: 'Defense', value: stats.defense,  max: stats.defMax,  color: 'var(--tier-rare)' },
    { label: 'Speed',   value: stats.speed,    max: stats.spdMax,  color: 'var(--tier-uncommon)' },
  ];

  return (
    <div style={{ minHeight: '100vh' }}>
      <div className="container" style={{ paddingTop: '1.5rem' }}>
        <button className="btn btn-secondary" onClick={onBack} style={{ fontSize: 'var(--t-xs)' }}>
          ← Back to Wiki
        </button>
      </div>

      {/* Hero */}
      <div className="wiki-detail-hero">
        <div className="wiki-detail-glow"
          style={{ background: `radial-gradient(circle, ${mob.glowColor ?? 'rgba(192,57,43,0.10)'} 0%, transparent 68%)` }} />
        <div className="container">
          <div className="wiki-detail-inner">
            <div className="wiki-detail-icon-box">{mob.icon}</div>
            <div className="wiki-detail-meta">
              <div className="wiki-detail-badges">
                <span className="wiki-cat-badge wiki-cat-mob">Mob</span>
                <span className={`tier-badge tier-${mob.tier}`}>
                  {RARITY_LABEL[mob.tier] ?? mob.tier}
                </span>
                {mob.type && <span className="wiki-type-tag">{mob.type}</span>}
                {mob.xp && <span className="wiki-xp-badge">✨ {mob.xp} XP</span>}
              </div>
              <h1 className="wiki-detail-name">{mob.name}</h1>
              <p className="wiki-detail-desc">{mob.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="container">
        <div className="wiki-detail-body">

          {/* Left: stats, abilities, lore */}
          <div>
            <div style={{ marginBottom: '2rem' }}>
              <div className="section-label"><span className="sl-text">Stats</span></div>
              <div className="card-raised">
                {statBars.map((s, i) => (
                  <StatBar key={i} label={s.label} value={s.value} max={s.max} color={s.color} />
                ))}
              </div>
            </div>

            {mob.abilities?.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <div className="section-label"><span className="sl-text">Abilities</span></div>
                {mob.abilities.map((ab, i) => <AbilityCard key={i} ability={ab} />)}
                <p style={{ fontSize: 'var(--t-xs)', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                  Click an ability to expand
                </p>
              </div>
            )}

            {mob.lore && (
              <div style={{ marginBottom: '2rem' }}>
                <div className="section-label"><span className="sl-text">Lore</span></div>
                <div className="wiki-lore">"{mob.lore}"</div>
              </div>
            )}
          </div>

          {/* Right: drops, spawn, tips */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {mob.drops?.length > 0 && (
              <div>
                <div className="section-label"><span className="sl-text">Drops</span></div>
                <div className="card-raised" style={{ padding: '0.25rem 1.25rem' }}>
                  {mob.drops.map((d, i) => (
                    <div key={i} className="wiki-drop-row">
                      <span className="wiki-drop-icon">{d.icon}</span>
                      <div className="wiki-drop-info">
                        <div className="wiki-drop-name">{d.name}</div>
                        {d.note && <div className="wiki-drop-note">{d.note}</div>}
                      </div>
                      <span className={`tier-badge tier-${d.rarity}`}>
                        {RARITY_LABEL[d.rarity] ?? d.rarity}
                      </span>
                      <span className="wiki-drop-chance">{d.chance}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {mob.spawn && (
              <div>
                <div className="section-label"><span className="sl-text">Spawn Info</span></div>
                <div className="card-raised" style={{ padding: '1rem 1.25rem' }}>
                  <div className="wiki-spawn-grid">
                    <div className="wiki-spawn-field">
                      <div className="wiki-spawn-label">Time</div>
                      <div className="wiki-spawn-value">{mob.spawn.time}</div>
                    </div>
                    <div className="wiki-spawn-field">
                      <div className="wiki-spawn-label">Spawn Rarity</div>
                      <div className="wiki-spawn-value">{mob.spawn.rarity}</div>
                    </div>
                  </div>

                  {mob.spawn.biomes?.length > 0 && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <div className="wiki-spawn-label" style={{ marginBottom: '5px' }}>Biomes</div>
                      <div className="wiki-spawn-chips">
                        {mob.spawn.biomes.map((b, i) => (
                          <span key={i} className="wiki-spawn-chip">{b.icon} {b.name}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {mob.spawn.locations?.length > 0 && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <div className="wiki-spawn-label" style={{ marginBottom: '5px' }}>Locations</div>
                      <div className="wiki-spawn-chips">
                        {mob.spawn.locations.map((l, i) => (
                          <span key={i} className="wiki-spawn-chip">{l.icon} {l.name}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {mob.spawn.method && (
                    <div className="wiki-spawn-method">{mob.spawn.method}</div>
                  )}
                </div>
              </div>
            )}

            {mob.tips?.length > 0 && (
              <div>
                <div className="section-label"><span className="sl-text">Combat Tips</span></div>
                <div className="card-raised" style={{ padding: '0.25rem 1.25rem' }}>
                  {mob.tips.map((tip, i) => (
                    <div key={i} className="wiki-tip">
                      <span className="wiki-tip-icon">💡</span>
                      {tip}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── DETAIL: Drop ─────────────────────────────────────────────
function DropDetail({ drop, onBack, onViewMob }) {
  const sourceMob = mobs.find(m => m.id === drop.sourceMobId);

  return (
    <div style={{ minHeight: '100vh' }}>
      <div className="container" style={{ paddingTop: '1.5rem' }}>
        <button className="btn btn-secondary" onClick={onBack} style={{ fontSize: 'var(--t-xs)' }}>
          ← Back to Wiki
        </button>
      </div>

      {/* Hero */}
      <div className="wiki-detail-hero">
        <div className="wiki-detail-glow"
          style={{ background: 'radial-gradient(circle, rgba(212,146,10,0.12) 0%, transparent 68%)' }} />
        <div className="container">
          <div className="wiki-detail-inner">
            <div className="wiki-detail-icon-box">{drop.icon}</div>
            <div className="wiki-detail-meta">
              <div className="wiki-detail-badges">
                <span className="wiki-cat-badge wiki-cat-drop">Drop</span>
                <span className={`tier-badge tier-${drop.rarity}`}>
                  {RARITY_LABEL[drop.rarity] ?? drop.rarity}
                </span>
              </div>
              <h1 className="wiki-detail-name">{drop.name}</h1>
              {drop.note && <p className="wiki-detail-desc">{drop.note}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="container" style={{ paddingBottom: '3rem' }}>
        <div style={{ maxWidth: '560px', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* Source mob */}
          <div>
            <div className="section-label"><span className="sl-text">Dropped By</span></div>
            <div
              className="card-raised wiki-source-card"
              onClick={() => onViewMob(drop.sourceMobId)}
            >
              <div className="wiki-source-icon-box">{drop.sourceMobIcon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--t-md)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
                  {drop.sourceMobName}
                </div>
                <span className={`tier-badge tier-${drop.sourceMobTier}`}>
                  {RARITY_LABEL[drop.sourceMobTier] ?? drop.sourceMobTier}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="wiki-source-chance">{drop.chance}</div>
                <div className="wiki-source-chance-label">drop chance</div>
              </div>
            </div>
            <p style={{ fontSize: 'var(--t-xs)', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
              Click to view mob →
            </p>
          </div>

          {/* Usage note */}
          {drop.note && (
            <div>
              <div className="section-label"><span className="sl-text">Usage</span></div>
              <div className="card-raised" style={{ padding: '0.75rem 1.25rem' }}>
                <div className="wiki-obtain-row" style={{ padding: 0, border: 'none' }}>
                  <span className="wiki-obtain-arrow">›</span>
                  {drop.note}
                </div>
              </div>
            </div>
          )}

          {/* Also used in items */}
          {(() => {
            const usedIn = ALL_ITEMS.filter(item =>
              item.recipe?.ingredients?.some(
                ing => ing.name.toLowerCase() === drop.name.toLowerCase()
              )
            );
            if (!usedIn.length) return null;
            return (
              <div>
                <div className="section-label"><span className="sl-text">Used In Recipes</span></div>
                <div className="card-raised" style={{ padding: '0.25rem 1.25rem' }}>
                  {usedIn.map((item, i) => (
                    <div key={i} className="wiki-obtain-row">
                      <span className="wiki-obtain-arrow">›</span>
                      <span>{itemIcon(item)}</span>
                      <span>{item.name}</span>
                      <span className={`tier-badge tier-${item.rarity}`} style={{ marginLeft: 'auto' }}>
                        {RARITY_LABEL[item.rarity]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ── LIST CARD: Item ──────────────────────────────────────────
function ItemCard({ item, onClick }) {
  const icon = itemIcon(item);
  return (
    <div className="card wiki-card" onClick={onClick}>
      <div className="wiki-card-bar" style={{ background: TIER_COLOR[item.rarity] ?? 'var(--accent)' }} />
      <div className="wiki-card-header">
        <div className="wiki-card-icon">{icon}</div>
        <div>
          <div className="wiki-card-name">{item.name}</div>
          <div className="wiki-card-badges">
            <span className={`tier-badge tier-${item.rarity}`}>
              {RARITY_LABEL[item.rarity] ?? item.rarity}
            </span>
            {item.type && <span className="wiki-card-type">{item.type}</span>}
          </div>
        </div>
      </div>
      <p className="wiki-card-desc">
        {item.description.slice(0, 90)}{item.description.length > 90 ? '…' : ''}
      </p>
      <div className="wiki-card-footer">
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {item.recipe && <span className="wiki-recipe-pill">Recipe</span>}
          {item.abilities?.length > 0 && (
            <span className="wiki-card-meta">
              {item.abilities.length} {item.abilities.length === 1 ? 'ability' : 'abilities'}
            </span>
          )}
        </div>
        <span className="wiki-card-cta">View →</span>
      </div>
    </div>
  );
}

// ── LIST CARD: Mob ───────────────────────────────────────────
function MobCard({ mob, onClick }) {
  return (
    <div className="card wiki-card" onClick={onClick}>
      <div className="wiki-card-bar" style={{ background: TIER_COLOR[mob.tier] ?? 'var(--accent)' }} />
      <div className="wiki-card-header">
        <div className="wiki-card-icon">{mob.icon}</div>
        <div>
          <div className="wiki-card-name">{mob.name}</div>
          <div className="wiki-card-badges">
            <span className={`tier-badge tier-${mob.tier}`}>
              {RARITY_LABEL[mob.tier] ?? mob.tier}
            </span>
            {mob.type && <span className="wiki-card-type">{mob.type}</span>}
          </div>
        </div>
      </div>
      <p className="wiki-card-desc">
        {mob.description.slice(0, 90)}{mob.description.length > 90 ? '…' : ''}
      </p>
      <div className="wiki-card-footer">
        <span className="wiki-card-meta">
          {mob.drops?.length ?? 0} drops · {mob.abilities?.length ?? 0} abilities
        </span>
        <span className="wiki-card-cta">View →</span>
      </div>
    </div>
  );
}

// ── LIST CARD: Drop ──────────────────────────────────────────
function DropCard({ drop, onClick }) {
  return (
    <div className="card wiki-card" onClick={onClick}>
      <div className="wiki-card-bar" style={{ background: TIER_COLOR[drop.rarity] ?? 'var(--teal)' }} />
      <div className="wiki-card-header">
        <div className="wiki-card-icon">{drop.icon}</div>
        <div>
          <div className="wiki-card-name">{drop.name}</div>
          <div className="wiki-card-badges">
            <span className={`tier-badge tier-${drop.rarity}`}>
              {RARITY_LABEL[drop.rarity] ?? drop.rarity}
            </span>
          </div>
        </div>
      </div>
      {drop.note && (
        <p className="wiki-card-desc">
          {drop.note.slice(0, 80)}{drop.note.length > 80 ? '…' : ''}
        </p>
      )}
      <div className="wiki-card-footer">
        <span className="wiki-card-meta">
          {drop.sourceMobIcon} {drop.sourceMobName} · {drop.chance}
        </span>
        <span className="wiki-card-cta">View →</span>
      </div>
    </div>
  );
}

// ── Filter constants ─────────────────────────────────────────
const TABS = [
  { id: 'all',   label: 'All' },
  { id: 'items', label: 'Items',  count: ALL_ITEMS.length },
  { id: 'mobs',  label: 'Mobs',   count: mobs.length      },
  { id: 'drops', label: 'Drops',  count: ALL_DROPS.length },
];

const ITEM_CATS  = ['All', ...new Set(ALL_ITEMS.map(i => i.category).filter(Boolean))];
const MOB_TIERS  = ['All', 'common', 'uncommon', 'rare', 'elite', 'epic', 'legendary', 'boss'];
const DROP_RARS  = ['All', 'common', 'uncommon', 'rare', 'epic', 'legendary'];

// ── MAIN WIKI PAGE ───────────────────────────────────────────
export default function Wiki() {
  const [tab,        setTab]        = useState('all');
  const [search,     setSearch]     = useState('');
  const [catFilter,  setCatFilter]  = useState('All');
  const [tierFilter, setTierFilter] = useState('All');
  const [rarFilter,  setRarFilter]  = useState('All');
  const [selected,   setSelected]   = useState(null); // { type, id }

  function select(type, id) {
    setSelected({ type, id });
    window.scrollTo(0, 0);
  }

  function deselect() {
    setSelected(null);
    window.scrollTo(0, 0);
  }

  // ── Filtering (must be before early returns) ───────────────
  const q = search.toLowerCase();

  const filteredItems = useMemo(() => {
    if (tab !== 'all' && tab !== 'items') return [];
    return ALL_ITEMS.filter(item => {
      const matchCat = catFilter === 'All' || item.category === catFilter;
      const matchQ   = !q || item.name.toLowerCase().includes(q)
        || item.type?.toLowerCase().includes(q)
        || item.category?.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [tab, catFilter, q]);

  const filteredMobs = useMemo(() => {
    if (tab !== 'all' && tab !== 'mobs') return [];
    return mobs.filter(mob => {
      const matchTier = tierFilter === 'All' || mob.tier === tierFilter;
      const matchQ    = !q || mob.name.toLowerCase().includes(q)
        || mob.type?.toLowerCase().includes(q);
      return matchTier && matchQ;
    });
  }, [tab, tierFilter, q]);

  const filteredDrops = useMemo(() => {
    if (tab !== 'all' && tab !== 'drops') return [];
    return ALL_DROPS.filter(drop => {
      const matchRar = rarFilter === 'All' || drop.rarity === rarFilter;
      const matchQ   = !q || drop.name.toLowerCase().includes(q)
        || drop.sourceMobName?.toLowerCase().includes(q);
      return matchRar && matchQ;
    });
  }, [tab, rarFilter, q]);

  // ── Detail views (after all hooks) ───────────────────────
  if (selected?.type === 'item') {
    const item = ALL_ITEMS.find(i => i.id === selected.id);
    if (item) return <ItemDetail item={item} onBack={deselect} />;
  }
  if (selected?.type === 'mob') {
    const mob = mobs.find(m => m.id === selected.id);
    if (mob) return <MobDetail mob={mob} onBack={deselect} />;
  }
  if (selected?.type === 'drop') {
    const drop = ALL_DROPS.find(d => d.id === selected.id);
    if (drop) return <DropDetail drop={drop} onBack={deselect} onViewMob={id => select('mob', id)} />;
  }

  const totalShowing = filteredItems.length + filteredMobs.length + filteredDrops.length;
  const totalAll     = ALL_ITEMS.length + mobs.length + ALL_DROPS.length;

  const showItemFilters = tab === 'all' || tab === 'items';
  const showMobFilters  = tab === 'all' || tab === 'mobs';
  const showDropFilters = tab === 'all' || tab === 'drops';

  // ── Render ────────────────────────────────────────────────
  return (
    <div style={{ paddingBottom: '3rem' }}>

      {/* ── Header ── */}
      <div className="wiki-header">
        <div className="wiki-header-glow" />
        <div className="container">
          <div className="wiki-eyebrow">SimpleMC</div>
          <h1>Wiki</h1>
          <p>
            Browse all custom items, mobs and drops. Click any card to see full stats,
            abilities, recipes and combat info.
          </p>

          {/* Tabs */}
          <div className="wiki-tabs">
            {TABS.map(t => (
              <button
                key={t.id}
                className={`wiki-tab-btn${tab === t.id ? ' active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                {t.count != null && (
                  <span className="wiki-tab-count">{t.count}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container">

        {/* ── Search ── */}
        <div className="wiki-search-row">
          <div className="wiki-search-wrap">
            <span className="wiki-search-icon">🔍</span>
            <input
              className="wiki-search"
              type="text"
              placeholder="Search items, mobs, drops…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* ── Filters ── */}
        <div className="wiki-filter-bar">
          {showItemFilters && ITEM_CATS.length > 1 && (
            <div className="wiki-filter-group">
              {ITEM_CATS.map(c => (
                <button
                  key={c}
                  className={`wiki-filter-btn${catFilter === c ? ' active-cat' : ''}`}
                  onClick={() => setCatFilter(c)}
                >
                  {c === 'All' ? 'All Categories' : c}
                </button>
              ))}
            </div>
          )}

          {showItemFilters && showMobFilters && <div className="wiki-filter-divider" />}

          {showMobFilters && (
            <div className="wiki-filter-group">
              {MOB_TIERS.map(t => (
                <button
                  key={t}
                  className={`wiki-filter-btn${tierFilter === t ? ' active' : ''}`}
                  onClick={() => setTierFilter(t)}
                >
                  {t === 'All' ? 'All Tiers' : t}
                </button>
              ))}
            </div>
          )}

          {(showItemFilters || showMobFilters) && showDropFilters && (
            <div className="wiki-filter-divider" />
          )}

          {showDropFilters && (
            <div className="wiki-filter-group">
              {DROP_RARS.map(r => (
                <button
                  key={r}
                  className={`wiki-filter-btn${rarFilter === r ? ' active' : ''}`}
                  onClick={() => setRarFilter(r)}
                >
                  {r === 'All' ? 'All Rarities' : r}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="wiki-count">
          Showing {totalShowing} of {totalAll} entries
        </div>

        {/* ── Items grid ── */}
        {filteredItems.length > 0 && (
          <>
            {tab === 'all' && <SectionDivider label="Items" />}
            <div className="grid-auto" style={{ marginBottom: tab === 'all' ? '2.5rem' : 0 }}>
              {filteredItems.map(item => (
                <ItemCard key={item.id} item={item} onClick={() => select('item', item.id)} />
              ))}
            </div>
          </>
        )}

        {/* ── Mobs grid ── */}
        {filteredMobs.length > 0 && (
          <>
            {tab === 'all' && <SectionDivider label="Mobs" />}
            <div className="grid-auto" style={{ marginBottom: tab === 'all' ? '2.5rem' : 0 }}>
              {filteredMobs.map(mob => (
                <MobCard key={mob.id} mob={mob} onClick={() => select('mob', mob.id)} />
              ))}
            </div>
          </>
        )}

        {/* ── Drops grid ── */}
        {filteredDrops.length > 0 && (
          <>
            {tab === 'all' && <SectionDivider label="Drops" />}
            <div className="grid-auto">
              {filteredDrops.map(drop => (
                <DropCard key={drop.id} drop={drop} onClick={() => select('drop', drop.id)} />
              ))}
            </div>
          </>
        )}

        {/* ── Empty state ── */}
        {totalShowing === 0 && (
          <div className="wiki-empty">
            No entries match your search or filters.
          </div>
        )}
      </div>
    </div>
  );
}
